import React from 'react';

/**
 * Splits a chat message into clickable runs.
 *
 * Detects, in order:
 *   - URLs (`https?://...`)
 *   - Bare domains with a recognised TLD (`myzow.com`, `example.co.in`)
 *   - Email addresses
 *   - Phone numbers in international (`+91 98765 43210`) or 10-digit shape
 *
 * Order matters: emails are caught before domains so `foo@bar.com` doesn't
 * partially match `bar.com`. URLs are caught first so a URL containing an
 * email or phone is not double-matched.
 *
 * Returns an array of React nodes ready to render inside a <p>. All anchors
 * carry `target="_blank"` + `rel="noopener noreferrer"` and stop propagation
 * so clicking a link doesn't trigger the surrounding bubble's reply gesture.
 */

// Combined matcher — capturing groups identify which entity matched.
// Group 1: full URL    | Group 2: email    | Group 3: bare domain
// Group 4: phone (international+digits OR 10-digit local)
const COMBINED_RE = new RegExp(
  [
    // URLs
    '(\\bhttps?:\\/\\/[^\\s<>"\']+)',
    // Emails (RFC-lite — good enough for chat)
    '([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,})',
    // Bare domain — at least one dot, ending in a 2-6 char TLD
    '(\\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,6}\\b)',
    // Phone: + or 0 prefix, OR a 10-digit run
    '(\\+\\d[\\d\\s\\-]{7,18}\\d|\\b\\d{10}\\b)',
  ].join('|'),
  'g'
);

const linkBaseClass =
  'underline decoration-dotted underline-offset-2 hover:decoration-solid hover:opacity-80 cursor-pointer break-all';

export const LinkifiedText = ({ text, className = '' }) => {
  if (!text || typeof text !== 'string') {
    return <span className={className}>{text}</span>;
  }

  const out = [];
  let lastIndex = 0;
  let key = 0;
  let m;
  COMBINED_RE.lastIndex = 0;

  while ((m = COMBINED_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      out.push(text.slice(lastIndex, m.index));
    }
    const [, url, email, domain, phone] = m;
    if (url) {
      out.push(
        <a
          key={`u${key++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkBaseClass}
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-link-url"
        >
          {url}
        </a>
      );
    } else if (email) {
      out.push(
        <a
          key={`e${key++}`}
          href={`mailto:${email}`}
          className={linkBaseClass}
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-link-email"
        >
          {email}
        </a>
      );
    } else if (domain) {
      // Skip domains preceded by '@' (would have been part of an email already)
      // — the regex order handles this; this guard is defensive only.
      out.push(
        <a
          key={`d${key++}`}
          href={`https://${domain}`}
          target="_blank"
          rel="noopener noreferrer"
          className={linkBaseClass}
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-link-domain"
        >
          {domain}
        </a>
      );
    } else if (phone) {
      // tel: links should drop spaces/dashes so dialers don't choke
      const dial = phone.replace(/[\s-]/g, '');
      out.push(
        <a
          key={`p${key++}`}
          href={`tel:${dial}`}
          className={linkBaseClass}
          onClick={(e) => e.stopPropagation()}
          data-testid="chat-link-phone"
        >
          {phone}
        </a>
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }

  return <span className={className}>{out}</span>;
};

export default LinkifiedText;
